import { Box, Dialog, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { CheckCircle } from '@material-ui/icons'
import { first, isEmpty, isFunction } from 'lodash'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { useSalesForceSubmitLead } from '../../hooks/salesforce'
import { clearSearch } from '../../redux/modules/locationSearch'
import { getAddressQueryStringParams } from '../../redux/modules/selectors'
import { locationRoute } from '../../routes/routes'
import { LocationType } from '../../util/constants'
import { formatUrl } from '../../util/formatUtils'
import RequestFoodsbyForm from '../location-search/RequestFoodsbyForm'
import SelectLocation from '../location-search/SelectLocation'

const darkPallete = Object.freeze({
  shade1: 'rgba(255, 255, 255, 0.87)',
  shade2: 'rgba(255, 255, 255, 0.6)',
  shade3: 'rgba(255, 255, 255, 0.38)',
  shade4: 'rgba(255, 255, 255, 0.12)',
  shade5: 'rgba(0, 0, 0, 0.87)',
})
const useStyles = makeStyles(() => ({
  addressContainer: {
    backgroundColor: darkPallete.shade5,
  },
  addressField: {
    '& .MuiInput-underline:after': {
      borderBottomColor: darkPallete.shade1,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset, &:hover fieldset': {
        borderColor: darkPallete.shade3,
      },
      '&.Mui-focused fieldset': {
        borderColor: darkPallete.shade1,
      },
    },
    '& hr': {
      background: darkPallete.shade4,
    },
    '& input, & .MuiRadio-root': {
      color: darkPallete.shade2,
    },
    '& input:-webkit-autofill': {
      // Handle ugly looking default auto fill styles.
      WebkitBoxShadow: `0 0 0 30px ${darkPallete.shade5} inset`,
      WebkitTextFillColor: darkPallete.shade1,
    },
    '& label': {
      color: darkPallete.shade3,
    },
    '& label.Mui-focused, & .MuiSelect-root, & .MuiInputBase-input, & .MuiSelect-icon': {
      color: darkPallete.shade1,
    },
  },
  addressText: {
    color: darkPallete.shade1,
  },
}))

export const DialogSelectLocation = ({ handleSelect, isScriptLoadSucceed, isScriptLoaded }) => {
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const { submitForm, success } = useSalesForceSubmitLead()
  const { address, error, lat, lng, loading, locations, noResults } = useSelector(
    state => state.locationSearch,
  )
  const addressParams = useSelector(state => getAddressQueryStringParams(state))

  // By default, push the location route
  const defaultHandleSelect = useCallback(
    location =>
      history.push(formatUrl(locationRoute.path, { locationId: location.deliveryLocationId })),
    [history],
  )

  // Auto select single result
  useEffect(() => {
    if (locations?.length === 1 && first(locations)?.type === LocationType.RESIDENTIAL_HOME) {
      isFunction(handleSelect)
        ? handleSelect(first(locations))
        : defaultHandleSelect(first(locations))
      dispatch(clearSearch())
    }
  }, [defaultHandleSelect, dispatch, handleSelect, locations])

  // Clear search on dialog close
  useEffect(() => {
    return () => dispatch(clearSearch())
  }, [dispatch])

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      onClose={() => dispatch(clearSearch())}
      open={!isEmpty(locations) || Boolean(noResults)}
    >
      <Grid container>
        <Grid item lg={9} md={8} xs={12}>
          <Box padding="16px">
            {isScriptLoaded && isScriptLoadSucceed && (
              <SelectLocation
                address={address}
                lat={address ? lat : undefined}
                lng={address ? lng : undefined}
                locations={locations}
                onClickOverride={location => {
                  isFunction(handleSelect) ? handleSelect(location) : defaultHandleSelect(location)
                  dispatch(clearSearch())
                }}
                showError={error}
                submitting={loading}
              />
            )}
          </Box>
        </Grid>
        <Grid className={classes.addressContainer} item lg={3} md={4} xs={12}>
          <Box padding="32px">
            {success ? (
              <Box alignItems="center" display="flex" flexDirection="column" textAlign="center">
                <CheckCircle style={{ color: '#6e8d28', fontSize: '60px' }} />
                <Typography className={classes.addressText} gutterBottom variant="h6">
                  Your request has been received!
                </Typography>
              </Box>
            ) : (
              <RequestFoodsbyForm
                city={addressParams?.city}
                error={error}
                fieldProps={{ className: classes.addressField }}
                handleSubmit={submitForm}
                state={addressParams?.state}
                street={addressParams?.street}
                textProps={{ className: classes.addressText }}
                zip={addressParams?.zip}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default DialogSelectLocation
